import React, { Component, ReactNode } from 'react';
import './Login.scss';
import { AuthLayout } from '../layouts/auth-layout/AuthLayout';
import Button from 'react-bootstrap/Button';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ERouterPath } from '../features/router/ERouterPath';
import { SingleUser } from '../api/types';
import { connect } from 'react-redux';
import { ApplicationState } from '../app/store';
import { Api } from '../api';
import { NotificationHelper } from '../helpers/NotificationHelper';
import { sessionActions } from '../app/session/actions';
import { DossierV2, IDossierV2 } from '../models/DossierV2';
import { DossierMember } from '../models/DossierMember';
import { ConfirmModal } from '../features/confirm-modal/ConfirmModal';

enum ENotificationType {
  NEW_CHAT = 'new_chat',
  NEW_MEASUREMENT = 'new_measurement',
  ANSWERED_MEASUREMENT = 'answered_measurement',
  NEW_PERSONAL_FILE = 'new_personal_file',
  NEW_DOSSIER_FILE = 'new_dossier_file',
}

interface INotificationPayload {
  dossierId: number,
  forUserId: number,
  displayUserId?: number,
  goTo: string,
  type: ENotificationType,
}

interface NotificationPageProps extends RouteComponentProps {
  dossier?: IDossierV2;
  user?: SingleUser;
  setActiveDossier: (dossier: DossierV2, dossierRelations: DossierMember[], relation?: DossierMember) => void;
}

interface NotificationPageState {
  requestDossierChangeTo?: number;
  switchingDossier?: boolean;
}

class NotificationPage extends Component<NotificationPageProps, NotificationPageState> {
  state: NotificationPageState = {};

  componentDidMount(): void {
    this.startRedirect();
  }

  startRedirect(): void {
    let data = undefined;
    try {
      data = this.getQueryData(this.props.location.search);
    } catch (err) {
      console.warn('Failed to get data from notification url query params', err);
    }

    if (data) {
      this.handleNotificationRedirect(data)
        .catch(err => {
          console.warn('Redirecting from navigation failed', err);
          this.handleReturnHome();
        });
    } else {
      this.handleReturnHome();
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async handleNotificationRedirect(data: INotificationPayload): Promise<void> {
    if (!data.forUserId || this.props.user?.id !== data.forUserId) {
      this.props.history.push(ERouterPath.login);
      return;
    }

    if (!data.dossierId || this.props.dossier?.id !== data.dossierId) {
      if (!data.dossierId) {
        throw new Error('Missing for dossier id, cannot process notification');
      }
      this.setState({ requestDossierChangeTo: data.dossierId });
      return;
    }

    this.props.history.push(data.goTo);
  }

  private getQueryData(query: string): INotificationPayload|undefined {
    const split = query.split('?data=');
    if (split.length === 2) {
      const substring = atob(split[1]);
      return JSON.parse(substring) as INotificationPayload;
    }
    return undefined;
  }

  handleReturnHome = (): void => {
    this.props.history.push(ERouterPath.dossier);
  };

  closeDossierModal = (): void => {
    this.setState({ requestDossierChangeTo: undefined });
  };

  switchToDossier = (dossierId: number): void => {
    this.setState({ switchingDossier: true });
    Api.getDossierWithUserRelation(dossierId)
      .then(({ dossier, dossierRelations, relation }) => {
        this.props.setActiveDossier(dossier, dossierRelations, relation);
        this.startRedirect();
      })
      .catch(err => {
        console.error('Failed to get dossier, is this user allowed to see this dossier?', err);
        NotificationHelper.toastError('Dossier kan niet worden geopend door een onverwachte fout.');
      })
      .finally(() => {
        this.setState({ switchingDossier: false, requestDossierChangeTo: undefined });
      });
  };

  render(): ReactNode {
    const { requestDossierChangeTo } = this.state;
    return (
      <AuthLayout tip="Moment, je wordt doorgestuurd...">
        <Button variant="link" onClick={this.handleReturnHome}>Terug naar home</Button>
        {
          !!requestDossierChangeTo &&
          <ConfirmModal
            title="Wisselen van dossier"
            body="Je hebt een notificatie geopend voor een ander dossier dan dat je nu open hebt. Wil je wisselen naar dat dossier?"
            cancelLabel="Nee, blijf op huidig dossier"
            confirmLabel="Ja, door naar andere dossier"
            confirmButtonStyle="danger"
            onCancel={this.handleReturnHome}
            onConfirm={() => this.switchToDossier(requestDossierChangeTo)}
          />
        }
      </AuthLayout>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    dossier: state.session.dossier,
    user: state.session.user,
  }),
  dispatch => ({
    setActiveDossier: (dossier: DossierV2, dossierRelations: DossierMember[], relation?: DossierMember) => dispatch(sessionActions.setDossier({ dossier, dossierRelations, relation })),
  })
)(withRouter(NotificationPage));
